import { useCallback, useEffect, useState } from "react";
import type { Preset } from "../../project/schema";
import { useServices } from "../context";
import { errorMessage } from "../../core/errors";
import { downloadText, readTextFile } from "../download";

/** Save / load / duplicate / rename / delete / import / export presets for one track. */
export function PresetBar({ trackId, kind }: { trackId: string; kind: Preset["instrument"]["kind"] }) {
  const { library, controller } = useServices();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const available = controller.storage !== null;

  const refresh = useCallback(async () => setPresets(await library.listPresets(kind)), [library, kind]);
  useEffect(() => {
    let alive = true;
    void library.listPresets(kind).then((l) => { if (alive) setPresets(l); });
    return () => { alive = false; };
  }, [library, kind]);

  const current = presets.find((p) => p.id === selected);
  if (!available) return <p className="hint">Presets need local storage, which is unavailable in this browser session.</p>;

  return (
    <div className="preset-bar" role="group" aria-label="Presets">
      <label className="inline-field">
        <span>Preset</span>
        <select value={selected} onChange={(e) => {
          const id = e.currentTarget.value;
          setSelected(id);
          const p = presets.find((x) => x.id === id);
          if (p) { library.applyPreset(trackId, p); setName(p.name); }
        }}>
          <option value="">{presets.length ? "Choose a preset…" : "No saved presets"}</option>
          {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <input aria-label="Preset name" placeholder="Preset name" value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
      <button type="button" onClick={async () => { const p = await library.savePresetFromTrack(trackId, name || "My preset"); await refresh(); if (p) setSelected(p.id); }}>Save new</button>
      <button type="button" disabled={!current} onClick={async () => { if (current) { await library.renamePreset(current, name); await refresh(); } }}>Rename</button>
      <button type="button" disabled={!current} onClick={async () => { if (current) { await library.duplicatePreset(current); await refresh(); } }}>Duplicate</button>
      <button type="button" disabled={!current} onClick={async () => {
        if (current && window.confirm(`Delete preset "${current.name}"?`)) { await library.deletePreset(current.id); setSelected(""); await refresh(); }
      }}>Delete</button>
      <button type="button" disabled={!current} onClick={() => { if (current) { const f = library.exportPreset(current); downloadText(f.filename, f.json); } }}>Export</button>
      <label className="file-button">
        Import
        <input type="file" accept=".json,application/json" className="sr-only" onChange={async (e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = "";
          if (!file) return;
          try { await library.importPreset(await readTextFile(file, 256 * 1024)); await refresh(); } catch (err) { controller.notify("error", errorMessage(err)); }
        }} />
      </label>
    </div>
  );
}
